import { useEffect, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Box, Button, Checkbox, Flex, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Spinner, Stack, Text, Textarea, useToast,
} from "@chakra-ui/react";
import { getSettlementEmail, sendSettlement, Settlement, SettlementEmailPreview } from "../../../services/settlementService";
import { crm } from "../../../theme";
import { brl } from "./duimp";

/** HTML do servidor (encoded + <br>) → texto editável; e de volta com escape. */
const htmlToText = (html: string) => {
  const doc = new DOMParser().parseFromString(html.replace(/<br\s*\/?>/gi, "\n"), "text/html");
  return doc.body.textContent ?? "";
};
const textToHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/\n/g, "<br>");

const splitEmails = (v: string) => v.split(/[;,\s]+/).map((s) => s.trim()).filter(Boolean);

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="6px">{children}</FormLabel>
);

const SettlementSendModal = ({ processId, settlement, onClose, onSent }: {
  processId: string; settlement: Settlement; onClose: () => void; onSent: () => void;
}) => {
  const toast = useToast();
  const [preview, setPreview] = useState<SettlementEmailPreview | null>(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachExcel, setAttachExcel] = useState(false);
  const [docs, setDocs] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [sending, setSending] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    getSettlementEmail(processId)
      .then((p) => {
        setPreview(p);
        setTo(p.to.join("; "));
        setCc(p.cc.join("; "));
        setSubject(p.subject);
        setBody(htmlToText(p.body));
        setDocs(new Set(p.linkedDocuments.map((d) => d.id)));
      })
      .catch(() => { toast({ status: "error", title: "Não foi possível preparar o e-mail" }); onClose(); });
  }, [processId]);

  const toggle = (id: string) => setDocs((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const attachments = (attachPdf ? 1 : 0) + (attachExcel ? 1 : 0) + docs.size;
  const pending = settlement.missingReceipts + settlement.unpaid + settlement.withoutValue;

  const send = async () => {
    setSending(true);
    try {
      await sendSettlement(processId, {
        to: splitEmails(to), cc: splitEmails(cc), subject, body: textToHtml(body),
        attachPdf, attachExcel, documentIds: Array.from(docs),
      });
      toast({ status: "success", title: "Prestação de contas enviada" });
      onSent();
    } catch (e: any) {
      toast({ status: "error", title: "Não foi possível enviar", description: e?.message, duration: 10000, isClosable: true });
    } finally {
      setSending(false);
      setConfirm(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="17px">Enviar prestação de contas · {settlement.cvfRef}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!preview ? (
            <Flex justify="center" py="32px"><Spinner color={crm.brand} /></Flex>
          ) : (
            <Stack spacing="14px">
              {pending > 0 && (
                <Box p="10px 12px" bg="#fffbeb" border="1px solid #fde68a" borderRadius="8px" fontSize="13px" color={crm.warning}>
                  Ainda há {pending} pendência(s) na conferência (comprovante, pagamento ou valor). Dá para enviar mesmo assim.
                </Box>
              )}
              <FormControl>
                <Label>Para (separe por ;)</Label>
                <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder={preview.to.length ? "" : "cliente sem contato com e-mail cadastrado"} />
              </FormControl>
              <FormControl>
                <Label>Cópia</Label>
                <Input value={cc} onChange={(e) => setCc(e.target.value)} />
              </FormControl>
              <FormControl>
                <Label>Assunto</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </FormControl>
              <FormControl>
                <Label>Mensagem</Label>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} fontSize="14px" />
              </FormControl>
              <Box>
                <Label>Anexos</Label>
                <Stack spacing="6px" fontSize="13px">
                  <Checkbox isChecked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)}>Demonstrativo em PDF</Checkbox>
                  <Checkbox isChecked={attachExcel} onChange={(e) => setAttachExcel(e.target.checked)}>Demonstrativo em Excel</Checkbox>
                  {preview.linkedDocuments.length > 0 && <Text fontSize="12px" fontWeight="700" color={crm.textMuted} pt="6px">Comprovantes vinculados</Text>}
                  {preview.linkedDocuments.map((d) => <Checkbox key={d.id} isChecked={docs.has(d.id)} onChange={() => toggle(d.id)}>{d.name}</Checkbox>)}
                  {preview.otherDocuments.length > 0 && <Text fontSize="12px" fontWeight="700" color={crm.textMuted} pt="6px">Outros documentos do processo</Text>}
                  {preview.otherDocuments.map((d) => <Checkbox key={d.id} isChecked={docs.has(d.id)} onChange={() => toggle(d.id)}>{d.name}</Checkbox>)}
                </Stack>
              </Box>
            </Stack>
          )}
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} isDisabled={!preview || splitEmails(to).length === 0} onClick={() => setConfirm(true)}>
            Enviar
          </Button>
        </ModalFooter>
      </ModalContent>

      <AlertDialog isOpen={confirm} leastDestructiveRef={cancelRef} onClose={() => setConfirm(false)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="16px">Enviar ao cliente?</AlertDialogHeader>
            <AlertDialogBody fontSize="14px">
              E-mail para {splitEmails(to).join(", ")}{splitEmails(cc).length ? ` (cópia: ${splitEmails(cc).length})` : ""} com {attachments} anexo(s).
              {" "}Saldo: {settlement.balanceDirection === "refund" ? `${brl(Math.abs(settlement.balance))} a devolver` : settlement.balanceDirection === "charge" ? `${brl(settlement.balance)} a cobrar` : "zerado"}.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setConfirm(false)}>Voltar</Button>
              <Button bg={crm.brand} color="white" _hover={{ bg: crm.brandDark }} ml={3} isLoading={sending} onClick={send}>Enviar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Modal>
  );
};

export default SettlementSendModal;
